import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, X, ChevronRight, ChevronLeft, Check, Loader, User } from 'lucide-react';
import { relationsApi } from '../../api/relations';
import { adminApi } from '../../api/admin';
import { recordsApi } from '../../api/records';
import ScreenRenderer from '../screens/ScreenRenderer';
import toast from 'react-hot-toast';
import { tagsApi } from '../../api/tags';

/**
 * IntakeWizard — Step 1 of record creation.
 * Search or create a linked record (e.g., Contact) before filling in the main record.
 * 
 * Props:
 *   linkField       — the RECORD_LINK field definition
 *   onComplete      — (linkedRecordId) => void — called when contact is selected/created
 *   onSkip          — () => void — if we allow skipping
 *   recordTypeName  — name of the parent record type being created
 */
export default function IntakeWizard({ linkField, onComplete, onSkip, recordTypeName }) {
  const config = linkField.config || {};
  const targetTypeId = config.target_record_type_id;
  const targetTypeName = config.target_type_name || 'Record';

  const [mode, setMode] = useState('search'); // search | create
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  // Create form state
  const [createFields, setCreateFields] = useState([]);
  const [createLayout, setCreateLayout] = useState(null);
  const [createData, setCreateData] = useState({});
  const [createLoading, setCreateLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const debounceRef = useRef(null);

  // Auto-tag contact based on the link field name (e.g. "Client" field → "Client" tag)
  // If tagged as Client, also generate a reference number
  const autoTagContact = async (contactRecordId) => {
    try {
      const tagName = linkField.label || linkField.name || '';
      const { tags } = await tagsApi.list('contact');
      const matchingTag = tags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (matchingTag) {
        await tagsApi.addToRecord(contactRecordId, matchingTag.id);
      }
    } catch (e) { /* silent - tagging is best-effort */ }
  };
  const searchInputRef = useRef(null);

  // Load fields for create mode
  useEffect(() => {
    if (!targetTypeId) return;
    const load = async () => {
      setCreateLoading(true);
      try {
        const [fieldsRes, screensRes] = await Promise.all([
          adminApi.getFields(targetTypeId),
          adminApi.getScreens(targetTypeId),
        ]);
        const fieldList = (fieldsRes.data?.fields || []).filter(f => !f.deleted_at);
        setCreateFields(fieldList);

        const screens = screensRes.data?.screens || [];
        const defaultScreen = screens.find(s => s.is_default) || screens[0];
        if (defaultScreen?.layout) setCreateLayout(defaultScreen.layout);

        // Set defaults
        const defaults = {};
        fieldList.forEach(f => {
          if (f.default_value !== null) defaults[f.id] = f.default_value;
          if (f.field_type === 'CHECKBOX' || f.field_type === 'TOGGLE') defaults[f.id] = false;
        });
        setCreateData(defaults);
      } catch (err) {
        console.error('Failed to load create fields:', err);
      } finally {
        setCreateLoading(false);
      }
    };
    load();
  }, [targetTypeId]);

  const doSearch = useCallback((q) => {
    if (!targetTypeId) return;
    setSearching(true);
    relationsApi.search(targetTypeId, q).then(data => {
      setResults(data.records || []);
    }).catch(() => {}).finally(() => setSearching(false));
  }, [targetTypeId]);

  useEffect(() => {
    // Initial load
    doSearch('');
  }, [doSearch]);

  const handleSearchChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  };

  const handleSelect = (record) => {
    setSelected(record);
  };

  const handleConfirmSelection = async () => {
    if (selected) {
      await autoTagContact(selected.id);
      onComplete(selected.id, selected);
    }
  };

  const handleCreate = async () => {
    // Validate required fields
    const required = createFields.filter(f => f.is_required);
    for (const f of required) {
      if (!createData[f.id] && createData[f.id] !== 0 && createData[f.id] !== false) {
        toast.error(f.label + ' is required');
        return;
      }
    }

    setSaving(true);
    try {
      const res = await recordsApi.create({
        record_type_id: targetTypeId,
        field_data: createData,
      });
      const record = res.data?.record || res.record;
      toast.success(targetTypeName + ' created');
      await autoTagContact(record.id);
      onComplete(record.id, record);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create ' + targetTypeName);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setCreateData(prev => ({ ...prev, [fieldId]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full text-xs font-semibold text-blue-600 mb-3">
          <span className="h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px]">1</span>
          Step 1 of 2
        </div>
        <h2 className="text-xl font-bold text-gray-900">
          {mode === 'search' ? `Select or Create ${targetTypeName}` : `New ${targetTypeName}`}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {mode === 'search'
            ? `Search for an existing ${targetTypeName.toLowerCase()} or create a new one.`
            : `Fill in the details for the new ${targetTypeName.toLowerCase()}.`}
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2 mb-5 bg-gray-100 rounded-xl p-1 max-w-xs mx-auto">
        <button onClick={() => setMode('search')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'search' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
          <Search className="h-3.5 w-3.5" /> Search
        </button>
        <button onClick={() => setMode('create')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'create' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
          <Plus className="h-3.5 w-3.5" /> Create New
        </button>
      </div>

      {/* ═══ Search Mode ═══ */}
      {mode === 'search' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Search bar */}
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input ref={searchInputRef} type="text" value={query} onChange={e => handleSearchChange(e.target.value)}
                placeholder={`Search by name, reference number...`} autoFocus
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto">
            {searching ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-8">
                <User className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No {targetTypeName.toLowerCase()}s found</p>
                <button onClick={() => setMode('create')}
                  className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-800">
                  + Create a new {targetTypeName.toLowerCase()}
                </button>
              </div>
            ) : (
              <div>
                {results.map(rec => (
                  <button key={rec.id} onClick={() => handleSelect(rec)}
                    className={`w-full text-left px-4 py-3.5 border-b border-gray-50 last:border-0 flex items-center gap-3 transition-all ${
                      selected?.id === rec.id
                        ? 'bg-blue-50 border-l-4 border-l-blue-500'
                        : 'hover:bg-gray-50'}`}>
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-semibold flex-shrink-0">
                      {(rec.title || '?')[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{rec.title || '(untitled)'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {rec.reference_number && (
                          <span className="text-[10px] text-gray-400 font-mono">{rec.reference_number}</span>
                        )}
                        {rec.stage_name && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                            style={{ backgroundColor: (rec.stage_color || '#6b7280') + '20', color: rec.stage_color || '#6b7280' }}>
                            {rec.stage_name}
                          </span>
                        )}
                      </div>
                    </div>
                    {selected?.id === rec.id && (
                      <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selection footer */}
          {selected && (
            <div className="p-4 border-t border-gray-200 bg-blue-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Selected: {selected.title}</span>
                <button onClick={() => setSelected(null)} className="text-blue-400 hover:text-blue-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <button onClick={handleConfirmSelection}
                className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ═══ Create Mode ═══ */}
      {mode === 'create' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6">
            {createLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : createLayout ? (
              <ScreenRenderer
                layout={createLayout}
                fields={createFields}
                fieldData={createData}
                onChange={handleFieldChange}
                errors={{}}
                readOnly={false}
              />
            ) : createFields.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No fields configured for {targetTypeName}.</p>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {createFields.filter(f => f.field_type !== 'FILE' && f.field_type !== 'RECORD_LINK').map(field => (
                  <div key={field.id} className={['TEXTAREA','RICH_TEXT'].includes(field.field_type) ? 'col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {renderCreateField(field, createData, handleFieldChange)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button onClick={() => setMode('search')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
              <ChevronLeft className="h-4 w-4" /> Back to Search
            </button>
            <button onClick={handleCreate} disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm">
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {saving ? 'Creating...' : `Create ${targetTypeName} & Continue`}
            </button>
          </div>
        </div>
      )}

      {/* Skip option */}
      {onSkip && (
        <div className="text-center mt-4">
          <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600">
            Skip — add {targetTypeName.toLowerCase()} later
          </button>
        </div>
      )}
    </div>
  );
}

function renderCreateField(field, data, onChange) {
  const baseClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

  if (field.field_type === 'TEXTAREA' || field.field_type === 'RICH_TEXT') {
    return <textarea value={data[field.id] || ''} onChange={e => onChange(field.id, e.target.value)}
      rows={3} className={baseClass} />;
  }
  if (field.field_type === 'DROPDOWN' && field.config?.options) {
    return (
      <select value={data[field.id] || ''} onChange={e => onChange(field.id, e.target.value)}
        className={baseClass + ' bg-white'}>
        <option value="">Select...</option>
        {field.config.options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    );
  }
  if (field.field_type === 'TOGGLE' || field.field_type === 'CHECKBOX') {
    return (
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={!!data[field.id]} onChange={e => onChange(field.id, e.target.checked)}
          className="rounded text-blue-600" />
        <span className="text-sm text-gray-600">{field.label}</span>
      </label>
    );
  }
  if (field.field_type === 'DATE') {
    return <input type="date" value={data[field.id] || ''} onChange={e => onChange(field.id, e.target.value)} className={baseClass} />;
  }
  return (
    <input type={field.field_type === 'EMAIL' ? 'email' : field.field_type === 'NUMBER' || field.field_type === 'CURRENCY' ? 'number' : 'text'}
      value={data[field.id] || ''} onChange={e => onChange(field.id, e.target.value)} className={baseClass} />
  );
}
